import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { ChartConfig } from '../../../types';
import { CHART_STYLE, formatAxisNumber } from './chartStyles';

interface HistogramViewProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
}

/**
 * Histogram — bins a single numeric column (Rule 5a: Numeric=1, rows > 50).
 */
export function HistogramView({ data, config }: HistogramViewProps) {
  const key = config.xAxis ?? '';

  const binned = useMemo(() => {
    const values = data
      .map((row) => row[key])
      .filter((v): v is number => typeof v === 'number' && isFinite(v));

    if (values.length === 0) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const binCount = Math.min(20, Math.ceil(Math.sqrt(values.length)));
    const binWidth = (max - min) / binCount || 1;

    const bins: { range: string; count: number }[] = [];
    for (let i = 0; i < binCount; i++) {
      const lo = min + i * binWidth;
      const hi = lo + binWidth;
      const label = `${formatAxisNumber(lo)}-${formatAxisNumber(hi)}`;
      const count = values.filter((v) => (i === binCount - 1 ? v >= lo && v <= hi : v >= lo && v < hi)).length;
      bins.push({ range: label, count });
    }
    return bins;
  }, [data, key]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={binned} margin={CHART_STYLE.margin}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis
          dataKey="range"
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.xAxis.tickFill }}
          angle={CHART_STYLE.xAxis.angle}
          textAnchor={CHART_STYLE.xAxis.textAnchor}
          height={CHART_STYLE.xAxis.height}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.yAxis.tickFill }}
          width={CHART_STYLE.yAxis.width}
          label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fontSize: CHART_STYLE.fontSize.axisTitle } }}
        />
        <Tooltip
          contentStyle={CHART_STYLE.tooltip}
          formatter={(value) => [Number(value ?? 0), 'Count']}
        />
        <Bar dataKey="count" fill={CHART_STYLE.colors[0]} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
